import { Button, Checkbox, FormControlLabel, TextField } from "@mui/material";
import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { DocumentTypesApiService } from "src/services/document-types";
import { toastifyResponse } from "src/utils/toastify-response/toastify-response";
import { v4 as uuidv4 } from "uuid";

import ConfirmationRemoveItemDialog from "./ConfirmationRemoveItemDialog/ConfirmationRemoveItemDialog";
import DocumentTransaction from "./DocumentTransaction/DocumentTransaction";
import { Props, State, INewFile } from "./interfaces";
import { ConfirmBlock, NewDocument, SaveButton } from "./styles";
import { handleUploadNewFiles, DocumentEvents } from "./utils";
import { useAppSelector } from "../../../custom-hooks/redux/redux";
import useAuthorization from "../../../custom-hooks/useAuthorization/useAuthorization";
import { CompanyLegalEntityRef } from "../../../models/company-accounts/company-legal-entities";
import { DocumentCategoryRef } from "../../../models/document-categories/document-category-ref";
import { DocumentTypeRef } from "../../../models/document-types";
import { DocumentDTO } from "../../../models/documents/document-dto";
import { DocumentFileRef } from "../../../models/documents/document-files";
import { TransactionDTO } from "../../../models/transactions";
import ExpandableItem from "../../../reusable-components/ExpandableItem/ExpandableItem";
import { handleDeleteDocumentFile } from "../../../services/documents/document-files-api.service";
import { loadDocuments } from "../../../services/documents/documents-api.service";
import { saveDocument } from "../../../services/documents/documents-api.service";
import { deleteDocument } from "../../../services/documents/documents-api.service";
import { saveTransactionDocument } from "../../../services/transactions-documents/transactions-documents-api.service";
import { AUTHORIZATIONS, ACCESS_TYPES } from "../../../utils/constants/authorization-constants";
import { DOCUMENTS_STATUS_TYPES } from "../../../utils/constants/constants";
import AddEditTransaction from "../../AddTransaction/AddEditTransaction/AddEditTransaction";
import LoadingOverlay from "../../LoadingOverlay/LoadingOverlay";
import DocumentFilesTable from "../DocumentFilesTable/DocumentFilesTable";
import DocumentFormfields from "../DocumentFormfields/DocumentFormfields";
import DocumentHeader from "../DocumentHeader/DocumentHeader";
import DragAndDropFiles from "../DragAndDropFiles/DragAndDropFiles";
import EditDocumentName from "../EditDocumentName/EditDocumentName";
import NewFile from "../NewFile/NewFile";

import { validateFile } from "../NewFile/utils";

import SimpleDocumentView from "../SimpleDocumentView/SimpleDocumentView";

const { PENDING, VERIFIED } = DOCUMENTS_STATUS_TYPES;
const { ProcessAccounting, ProcessIncomingDocuments } = AUTHORIZATIONS.LegalEntityAccess;

const Document = ({
  documentName,
  isPredefinedName,
  documentID,
  transaction,
  isWidget = false,
  handleNotify,
  shouldReload,
  documentFieldsProps,
  allowMarkVerified,
  hideAllowMarkVerified,
  relation,
  changeDateOrPeriodOnly = false,
  selectedRows,
  relationHandler,
  readOnlyFields,
  displayExpandableItemUponSave = true,
  handleControlledExpand,
  expanded = false,
  handleConfirm,
  filesProps,
  hideReadOnlyFields = false,
  useSimpleView = false,
  maxFilesNumber,
  simpleViewAlert,
  deleteDocumentIfEmpty = false,
  forceClose,
  documentIsReadOnly,
  auditTypeIds = [],
  customDocAndFilesNames,
  customSaveTitle,
  onlyAcceptedExtensions,
  customSaveLogic,
  checkIfFileDeletable,
  allowShare = true,
  isGenericDocument,
  allowDownloadContent,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.upload_document"; // tRP - translation root path

  const { currentCompanyAccount } = useAppSelector((state) => state.currentUserReducer);

  const allowMarkFileAsVerified = useAuthorization([
    ProcessAccounting,
    ProcessIncomingDocuments,
    ACCESS_TYPES.CHANGE_ALL.id,
  ]);

  const defaultDocument = {
    companyAccount: currentCompanyAccount,
    createdAt: new Date(0, 0, 1),
    createdByUser: null,
    isDeleted: false,
    id: documentID ?? uuidv4(),
    name: documentName ?? "",
    legalEntity: null,
    employee: null,
    employeeGroup: null,
    client: null,
    vendor: null,
    category: null,
    type: null,
    status: allowMarkFileAsVerified ? VERIFIED : PENDING,
    published: false,
    filesCount: 0,
    files: [],
    clarifications: [],
    documentTransactions: [],
    period: null,
    documentDate: null,
    ...(documentFieldsProps ? documentFieldsProps : {}),
  };

  const [state, setState] = useState<State>({
    document: {
      companyAccount: currentCompanyAccount,
      isDeleted: false,
      createdAt: new Date(),
      createdByUser: null,
      id: documentID ?? uuidv4(),
      name: documentName ?? "",
      legalEntity: null,
      employee: null,
      employeeGroup: null,
      client: null,
      vendor: null,
      category: null,
      type: null,
      status: VERIFIED,
      published: false,
      filesCount: 0,
      files: [],
      clarifications: [],
      documentTransactions: [],
      period: null,
      documentDate: new Date(),
      ...(documentFieldsProps ? documentFieldsProps : {}),
    },
    transaction: transaction ?? null,
    isNewDocument: !documentID,
    newFiles: [...(filesProps ? filesProps : [])],
    loading: false,
    error: "",
  });

  //#region change document field
  const handleChangeDocumentField = (field: keyof DocumentDTO, value: DocumentDTO[keyof DocumentDTO]): void => {
    if (field === "legalEntity" && !!value) {
      setState((state) => ({
        ...state,
        document: {
          ...state.document,
          [field]: value as CompanyLegalEntityRef, // as DocumentCategoryRef
          // type: null,
          client: null,
          vendor: null,
          employee: null,
        },
      }));
    } else if (field === "category") {
      setState((state) => ({
        ...state,
        document: {
          ...state.document,
          [field]: value as DocumentCategoryRef,
          type: null,
          client: null,
          vendor: null,
        },
      }));
    } else if (field === "type") {
      setState((state) => ({
        ...state,
        document: {
          ...state.document,
          [field]: value as DocumentTypeRef,
          client: null,
          vendor: null,
          // employee: (value as DocumentTypeRef)?.requestEeId // TODO clear out should we remove employee
          //   ? state.document?.employee
          //   : null,
        },
      }));
    } else {
      setState((state) => ({
        ...state,
        document: { ...state.document, [field]: value },
      }));
    }

    if (handleNotify) {
      handleNotify(DocumentEvents.DOCUMENT_IS_MUTATED, state.document);
    }
  };
  //#endregion
  //#region change document status
  const handleChangeDocumentStatus = (docStatusId: string) => {
    const type = VERIFIED.id === docStatusId ? "VERIFIED" : "PENDING";

    handleChangeDocumentField("status", DOCUMENTS_STATUS_TYPES[type]);
  };
  //#endregion

  //#region set new files
  const setNewFiles = (files: FileList | null) => {
    const newFiles: INewFile[] = [];

    if (files) {
      for (const file of files) {
        if (validateFile(file)) {
          newFiles.push({
            file,
            fileName: file.name.split(".").slice(0, -1).join("."),
          });
        }
      }
    }

    if (newFiles.length)
      setState((state) => ({
        ...state,
        newFiles: [...state.newFiles, ...newFiles],
      }));

    handleNotify && handleNotify(DocumentEvents.DOCUMENT_IS_MUTATED, state.document);
  };
  //#endregion

  //#region handle remove new file
  const handleRemoveNewFile = (idx: number) => {
    setState((state) => ({
      ...state,
      newFiles: state.newFiles.filter((_, index: number) => index !== idx),
    }));
  };
  //#endregion
  //#region change file name
  const handleChangeFileName = (value: string, idx: number): void =>
    setState((state) => ({
      ...state,
      newFiles: state.newFiles.map((file, index) => (index === idx ? { ...file, fileName: value } : file)),
    }));
  //#endregion

  //#region save document callback
  const handleSaveDocumentCallback = async (transaction?: TransactionDTO, newDocName?: string) => {
    setState((state) => ({ ...state, loading: true }));
    try {
      if (selectedRows && selectedRows.length > 0) {
        for (let i = 0; i < selectedRows.length; i++) {
          const response = await saveDocument(
            newDocName ? { ...state.document, name: newDocName } : { ...state.document },
            undefined,
            undefined,
            isGenericDocument
          );

          const invokerId = response.invokerId;
          const correlationId = response.correlationId;

          await handleUploadNewFiles(state.document, state.newFiles, invokerId, correlationId, isGenericDocument);

          if (transaction) {
            await saveTransactionDocument(transaction, state.document, correlationId);
          } else if (state.transaction) {
            await saveTransactionDocument(state.transaction, state.document, correlationId);
          }

          if (relationHandler) {
            await relationHandler(selectedRows, state.document, i);
          }

          state.document.id = uuidv4();
        }

        setState((state) => ({
          ...state,
          loading: false,
          isNewDocument: false,
          newFiles: [],
          document: defaultDocument,
        }));

        shouldReload && shouldReload(true);
        return;
      } else {
        const response = await saveDocument(
          newDocName ? { ...state.document, name: newDocName } : state.document,
          undefined,
          undefined,
          isGenericDocument
        );

        const invokerId = response.invokerId;
        const correlationId = response.correlationId;

        await handleUploadNewFiles(state.document, state.newFiles, invokerId, correlationId, isGenericDocument);

        if (transaction) {
          await saveTransactionDocument(transaction, state.document, correlationId);
        } else if (state.transaction) {
          await saveTransactionDocument(state.transaction, state.document, correlationId);
        }

        if (relation) {
          await relation.relationHandler(relation.relatedEntity, state.document);
        }
      }
      //this logic is implemented to allow the user to see the uploaded document when not authorized for GetDocument
      //hint : when not authorized for GetDocuments => isSucceded: true, error: "", items: []
      const { items, error, isSucceeded } = await loadDocuments(
        undefined,
        state.document.id,
        undefined,
        undefined,
        undefined,
        isGenericDocument
      );
      if (!error) {
        if (items[0]) {
          if (displayExpandableItemUponSave && !!items[0]) {
            setState((state) => ({
              ...state,
              loading: false,
              isNewDocument: false,
              newFiles: [],
              document: items[0],
            }));
          } else if (items[0]) {
            setState((state) => ({
              ...state,
              document: items[0],
            }));
          }
          handleNotify && handleNotify(DocumentEvents.DOCUMENT_IS_SAVED, items[0]);

          shouldReload && shouldReload(true);
        } else if (isSucceeded) {
          const new_document = {
            ...state.document,
            filesCount: state.document.files.length + state.newFiles.length,
          };
          if (displayExpandableItemUponSave) {
            setState((state) => ({
              ...state,
              loading: false,
              isNewDocument: false,
              newFiles: [],
              document: new_document,
            }));
          } else if (items[0]) {
            setState((state) => ({
              ...state,
              document: new_document,
            }));
          }
          handleNotify && handleNotify(DocumentEvents.DOCUMENT_IS_SAVED, new_document);

          shouldReload && shouldReload(true);
        }
      } else {
        setState((state) => ({
          ...state,
          error,
        }));
      }
    } catch (error: any) {
      setState((state) => ({ ...state, loading: false, error }));
    }
  };
  //#endregion

  //#region save with custom logic
  const handleSaveDocumentWithCustomLogic = async (transaction?: TransactionDTO, newDocName?: string) => {
    const newDocument = {
      ...state.document,
      name: newDocName ?? state.document.name,
    };

    customSaveLogic && customSaveLogic(newDocument as DocumentDTO, state.newFiles);
  };
  //#endregion

  //#region confirm document
  const handleConfirmDocument = () => {
    handleConfirm &&
      handleConfirm({
        document: state.document,
        newFiles: state.newFiles,
      });
  };
  //#endregion
  //#region reset document
  const handleResetDocument = () => {
    handleConfirm &&
      handleConfirm({
        document: null,
        newFiles: [],
      });
  };
  //#endregion
  //#region delete document
  const handleDeleteDocumentCallback = async (forcedDeleteDocument?: boolean) => {
    const confirmed: boolean | null = !forcedDeleteDocument
      ? window.confirm(`${t(`${tRP}.delete_document_confirmation_message`)}${state.document.name}"?`)
      : null;

    if (forcedDeleteDocument || confirmed) {
      setState((state) => ({ ...state, loading: true }));

      try {
        await deleteDocument(state.document, isGenericDocument);

        if (handleNotify) {
          setState((state) => ({ ...state, loading: false }));

          handleNotify(DocumentEvents.DOCUMENT_IS_DELETED, state.document);
        } else {
          setState((state) => ({
            ...state,
            document: {
              companyAccount: currentCompanyAccount,
              isDeleted: false,
              createdAt: new Date(),
              createdByUser: null,
              id: uuidv4(),
              name: documentName ?? "",
              legalEntity: null,
              employee: null,
              employeeGroup: null,
              client: null,
              vendor: null,
              category: null,
              type: null,
              status: allowMarkFileAsVerified ? VERIFIED : PENDING,
              published: false,
              filesCount: 0,
              files: [],
              clarifications: [],
              documentTransactions: [],
              period: null,
              documentDate: null,
              ...(documentFieldsProps ? documentFieldsProps : {}),
            },
            isNewDocument: false,
            loading: false,
          }));
        }
      } catch (error: any) {
        setState((state) => ({ ...state, loading: false, error }));
      }
    }
  };
  //#endregion
  //#region delete document file
  const handleDeleteDocumentFileCallback = async (file: DocumentFileRef) => {
    const confirmed = window.confirm(`${t(`${tRP}.document_files_table.delete_confirmation_message`)} ${file.name}?`);

    if (confirmed) {
      setState((state) => ({ ...state, loading: true }));

      try {
        await handleDeleteDocumentFile(file, state.document);
        //this logic is implemented to allow the user to see the updated document when not authorized for GetDocument
        //hint : when not authorized for GetDocuments => isSucceded: true, error: "", items: []

        const { items, error, isSucceeded } = await loadDocuments(
          undefined,
          state.document.id,
          undefined,
          undefined,
          undefined,
          isGenericDocument
        );
        if (!error) {
          if (state.document.filesCount - 1 === 0 && state.newFiles.length === 0 && deleteDocumentIfEmpty) {
            handleDeleteDocumentCallback(true);
          } else {
            if (isSucceeded && !!items[0]) {
              handleNotify && handleNotify(DocumentEvents.DOCUMENT_IS_SAVED, items[0]);
              setState((state) => ({ ...state, document: items[0] }));
            } else if (isSucceeded && !items[0]) {
              handleNotify &&
                handleNotify(DocumentEvents.DOCUMENT_IS_SAVED, {
                  ...state.document,
                  files: state.document.files.filter((_file) => _file.id !== file.id),
                  filesCount: state.document.filesCount - 1,
                });
              setState((state) => ({
                ...state,
                document: {
                  ...state.document,
                  files: state.document.files.filter((_file) => _file.id !== file.id),
                  filesCount: state.document.filesCount - 1,
                },
              }));
            }
          }
        } else {
          setState((state) => ({ ...state, error }));
        }
      } catch (error: any) {
        setState((state) => ({ ...state, error }));
      } finally {
        setState((state) => ({ ...state, loading: false }));
      }
    }
  };
  //#endregion

  //#region document
  const loadDocumentCallback = useCallback(async (id: string) => {
    setState((state) => ({ ...state, loading: true, isNewDocument: false }));

    try {
      const { items, error, isSucceeded } = await loadDocuments(
        undefined,
        id,
        undefined,
        undefined,
        undefined,
        isGenericDocument
      );

      //this logic is implemented to allow the user to see the uploaded document when not authorized for GetDocument
      //hint : when not authorized for GetDocuments => isSucceded: true, error: "", items: []

      if (error) {
        setState((state) => ({
          ...state,
          error,
        }));
      } else if (isSucceeded && !!items[0]) {
        setState((state) => ({
          ...state,
          loading: false,
          document: items[0],
          error: "",
        }));
      } else if (isSucceeded && !items[0]) {
        setState((state) => ({
          ...state,
          loading: false,
        }));
        handleNotify && handleNotify(DocumentEvents.DOCUMENT_IS_NOT_AUTHORIZED, null);
        toast.warning(t(`${tRP}.document_view_non_authorized`));
      }
    } catch (error: any) {
      setState((state) => ({ ...state, loading: false, error }));
    }
  }, []);
  //#endregion

  //#region notify controlled exapnd
  const notifyControlledExpand = () => {
    if (documentID) {
      handleControlledExpand && handleControlledExpand(documentID);
    }
  };
  //#endregion

  //#region load document type
  const loadDocumentTypeCallback = useCallback(async () => {
    if (documentFieldsProps?.type?.id) {
      setState((state) => ({
        ...state,
        loading: true,
      }));
      try {
        const response = await new DocumentTypesApiService().loadItems(
          { ids: [documentFieldsProps.type.id] },
          undefined,
          [],
          undefined,
          undefined,
          60
        );
        if (!response.error) {
          setState((state) => ({
            ...state,
            document: {
              ...state.document,
              type: response.items[0] as DocumentTypeRef | null,
            },
          }));
        }
        toastifyResponse(response, "failed");
      } catch (error: any) {
        setState((state) => ({
          ...state,
          loading: false,
          error: error?.message ?? error,
        }));
      } finally {
        setState((state) => ({
          ...state,
          loading: false,
        }));
      }
    }
  }, [documentFieldsProps?.type?.id]);
  //#endregion

  useEffect(() => {
    if (documentID) {
      loadDocumentCallback(documentID);
    }
  }, [loadDocumentCallback, documentID]);

  useEffect(() => {
    if (!documentID && documentFieldsProps?.type?.id) {
      loadDocumentTypeCallback();
    }
  }, [loadDocumentCallback, documentID]);

  return (
    <LoadingOverlay loading={state.loading}>
      {state.isNewDocument ? (
        <NewDocument>
          {!isWidget && (
            <>
              <DocumentFormfields
                document={state.document}
                isNewDocument={state.isNewDocument}
                handleChangeDocumentField={handleChangeDocumentField}
                changeDateOrPeriodOnly={changeDateOrPeriodOnly}
                readOnlyFields={readOnlyFields}
                hideReadOnlyFields={hideReadOnlyFields}
                auditTypeIds={auditTypeIds}
              />

              {!!state.transaction && !changeDateOrPeriodOnly ? (
                <DocumentTransaction transaction={state.transaction} />
              ) : (
                state.document.type &&
                state.document.type.requiredTransactionType && (
                  <AddEditTransaction
                    effect={"add"}
                    buttonVariant={"text"}
                    returnTransaction={true}
                    handleNotifyParent={(transaction) => handleSaveDocumentCallback(transaction)}
                    legalEntity={documentFieldsProps?.legalEntity as CompanyLegalEntityRef}
                  />
                )
              )}
            </>
          )}

          <TextField
            fullWidth
            value={state.document.name}
            disabled={isPredefinedName}
            placeholder={t(`${tRP}.document_name_textfield_placeholder`)}
            onChange={(e) => handleChangeDocumentField("name", e.target.value)}
          />
          <DragAndDropFiles
            handleSetNewFiles={setNewFiles}
            maxFilesNumberReached={!!maxFilesNumber && state.newFiles.length === maxFilesNumber}
            onlyAcceptedExtensions={onlyAcceptedExtensions}
          >
            {state.newFiles.map(({ file, fileName }, idx) => (
              <NewFile
                key={idx}
                file={file}
                fileName={fileName}
                handleChangeFileName={(name) => handleChangeFileName(name, idx)}
                handleRemoveFile={() => handleRemoveNewFile(idx)}
              />
            ))}
          </DragAndDropFiles>

          {allowMarkVerified && allowMarkFileAsVerified && (
            <FormControlLabel
              label={`${t(`${tRP}.mark_document_as_verified_checkbox`)}`}
              control={
                <Checkbox
                  checked={state.document.status?.id === VERIFIED.id}
                  onChange={() => handleChangeDocumentStatus(state.document.status?.id ?? "")}
                />
              }
              sx={{ visibility: hideAllowMarkVerified ? "hidden" : "visible" }}
            />
          )}

          <SaveButton>
            {!handleConfirm ? (
              <Button
                variant="contained"
                disabled={!state.newFiles.length}
                onClick={() => (customSaveLogic ? handleSaveDocumentWithCustomLogic() : handleSaveDocumentCallback())}
              >
                {`${customSaveTitle ?? t("buttons.save")}`}
              </Button>
            ) : (
              <ConfirmBlock>
                {!!filesProps?.length && (
                  <ConfirmationRemoveItemDialog handleRemove={handleResetDocument}>
                    <Button
                      variant="contained"
                      color="error"
                      disabled={!state.newFiles.length}
                    >
                      {`${t("buttons.reset")}`}
                    </Button>
                  </ConfirmationRemoveItemDialog>
                )}
                <Button
                  variant="contained"
                  disabled={!state.newFiles.length}
                  onClick={() => handleConfirmDocument()}
                >
                  {`${t("buttons.confirm")}`}
                </Button>
              </ConfirmBlock>
            )}
          </SaveButton>
        </NewDocument>
      ) : (
        <ExpandableItem
          useSimpleView={useSimpleView}
          notifyControlledExpand={notifyControlledExpand}
          // expanded={(!!expanded ?? false) || state.document.filesCount === 0} // initial value
          expanded={expanded || useSimpleView}
          expanderContainer={(expanded) =>
            expanded ? (
              <EditDocumentName
                document={state.document}
                saveDocument={(name) =>
                  customSaveLogic
                    ? handleSaveDocumentWithCustomLogic(undefined, name)
                    : handleSaveDocumentCallback(undefined, name)
                }
                deleteDocument={handleDeleteDocumentCallback}
                documentIsReadOnly={documentIsReadOnly}
                customDocAndFilesNames={customDocAndFilesNames}
                allowShare={allowShare}
                allowDownloadContent={allowDownloadContent}
                isGenericDocument={isGenericDocument}
              />
            ) : (
              <DocumentHeader
                document={state.document}
                deleteDocument={handleDeleteDocumentCallback}
                customDocAndFilesNames={customDocAndFilesNames}
                isGenericDocument={isGenericDocument}
                allowDownloadContent={allowDownloadContent}
              />
            )
          }
          expandedContainer={
            !useSimpleView ? (
              <>
                {!isWidget && (
                  <>
                    <DocumentFormfields
                      document={state.document}
                      handleChangeDocumentField={handleChangeDocumentField}
                      readOnlyFields={readOnlyFields}
                      hideReadOnlyFields={hideReadOnlyFields}
                      auditTypeIds={auditTypeIds}
                    />

                    {state.document?.documentTransactions.map(({ transaction }) => {
                      return (
                        <AddEditTransaction
                          key={transaction?.id}
                          effect="edit"
                          item={(transaction ? { ...transaction, transactionDocuments: [] } : transaction) as any}
                          handleNotifyParent={() => forceClose && forceClose()}
                        >
                          <DocumentTransaction
                            key={transaction?.id}
                            transaction={transaction}
                          />
                        </AddEditTransaction>
                      );
                    })}
                  </>
                )}

                <DocumentFilesTable
                  document={state.document}
                  handleDeleteFile={handleDeleteDocumentFileCallback}
                  isWidget={isWidget}
                />
                <DragAndDropFiles
                  handleSetNewFiles={setNewFiles}
                  onlyAcceptedExtensions={onlyAcceptedExtensions}
                >
                  {state.newFiles.map(({ file, fileName }, idx: number) => (
                    <NewFile
                      key={idx}
                      file={file}
                      fileName={fileName}
                      handleChangeFileName={(name) => handleChangeFileName(name, idx)}
                      handleRemoveFile={() => handleRemoveNewFile(idx)}
                    />
                  ))}
                </DragAndDropFiles>
                <SaveButton>
                  <Button
                    variant="contained"
                    onClick={() =>
                      customSaveLogic ? handleSaveDocumentWithCustomLogic() : handleSaveDocumentCallback()
                    }
                  >
                    {`${customSaveTitle ?? t("buttons.save")}`}
                  </Button>
                </SaveButton>
              </>
            ) : (
              <>
                {!documentIsReadOnly ? (
                  <DragAndDropFiles
                    handleSetNewFiles={setNewFiles}
                    maxFilesNumberReached={
                      !!maxFilesNumber &&
                      (state.document.files.length >= maxFilesNumber || state.newFiles.length >= maxFilesNumber)
                    }
                    customStyles={{ marginTop: "10px" }}
                    onlyAcceptedExtensions={onlyAcceptedExtensions}
                  >
                    <SimpleDocumentView
                      document={state.document}
                      handleDeleteFile={handleDeleteDocumentFileCallback}
                      simpleViewAlert={simpleViewAlert}
                      checkIfFileDeletable={checkIfFileDeletable}
                    />
                    {state.newFiles.length > 0 && (
                      <>
                        {state.newFiles.map(({ file, fileName }, idx: number) => (
                          <NewFile
                            key={idx}
                            file={file}
                            fileName={fileName}
                            handleChangeFileName={(name) => handleChangeFileName(name, idx)}
                            handleRemoveFile={() => handleRemoveNewFile(idx)}
                          />
                        ))}
                        <SaveButton>
                          <Button
                            variant="contained"
                            onClick={() =>
                              customSaveLogic ? handleSaveDocumentWithCustomLogic() : handleSaveDocumentCallback()
                            }
                          >
                            {`${customSaveTitle ?? t("buttons.save")}`}
                          </Button>
                        </SaveButton>
                      </>
                    )}
                  </DragAndDropFiles>
                ) : (
                  <SimpleDocumentView
                    document={state.document}
                    handleDeleteFile={handleDeleteDocumentFileCallback}
                    simpleViewAlert={simpleViewAlert}
                    documentIsReadOnly={documentIsReadOnly}
                    checkIfFileDeletable={checkIfFileDeletable}
                    isGenericDocument={isGenericDocument}
                  />
                )}
              </>
            )
          }
        />
      )}
    </LoadingOverlay>
  );
};

export default Document;
